import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { User, Bell, Lock, Globe, Trash2, Upload, Camera, Moon, Sun, Shield } from 'lucide-react';

export const Settings = () => {
    // Get tab from URL query parameter
    const getInitialTab = () => {
        const params = new URLSearchParams(window.location.search);
        const tab = params.get('tab');
        return tab === 'account' ? 'account' : 'profile';
    };

    const [activeTab, setActiveTab] = useState(getInitialTab());
    const [profileImage, setProfileImage] = useState(null);
    const [darkMode, setDarkMode] = useState(false);
    const [notifications, setNotifications] = useState({
        email: true,
        push: false,
        analysis: true,
    });

    // Update URL when tab changes
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        params.set('tab', activeTab);
        window.history.replaceState({}, '', `${window.location.pathname}?${params}`);
    }, [activeTab]);

    const [profile, setProfile] = useState({
        name: 'Roger Smith',
        email: 'roger.smith@example.com',
        phone: '+1 (555) 123-4567',
        company: 'Investment Corp',
        jobTitle: 'Senior Analyst',
        bio: '',
    });

    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setProfileImage(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSaveProfile = () => {
        alert('Profile settings saved!');
    };

    const handleSaveNotifications = () => {
        alert('Notification settings saved!');
    };

    const handleChangePassword = () => {
        alert('Password change functionality would be implemented here');
    };

    const handleDeleteAccount = () => {
        if (window.confirm('Are you sure you want to delete your account? This action cannot be undone.')) {
            alert('Account deletion functionality would be implemented here');
        }
    };

    return (
        <DashboardLayout title="Settings">
            <div className="max-w-5xl mx-auto">
                {/* Tabs Navigation */}
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 mb-6">
                    <div className="flex border-b border-slate-200">
                        <button
                            onClick={() => setActiveTab('profile')}
                            className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
                                activeTab === 'profile'
                                    ? 'text-primary-600 border-b-2 border-primary-600 bg-primary-50/50'
                                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                            }`}
                        >
                            <User className="h-5 w-5 inline-block mr-2" />
                            Profile Settings
                        </button>
                        <button
                            onClick={() => setActiveTab('account')}
                            className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
                                activeTab === 'account'
                                    ? 'text-primary-600 border-b-2 border-primary-600 bg-primary-50/50'
                                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                            }`}
                        >
                            <Shield className="h-5 w-5 inline-block mr-2" />
                            Account Settings
                        </button>
                    </div>
                </div>

                {/* Profile Settings Tab */}
                {activeTab === 'profile' && (
                    <div className="space-y-6 animate-fade-in">
                        {/* Profile Picture */}
                        <Card className="p-6">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="h-10 w-10 bg-primary-100 rounded-lg flex items-center justify-center">
                                    <Camera className="h-5 w-5 text-primary-600" />
                                </div>
                                <div>
                                    <h2 className="text-xl font-bold text-slate-900">Profile Picture</h2>
                                    <p className="text-sm text-slate-500">Upload your profile photo</p>
                                </div>
                            </div>

                            <div className="flex items-center gap-6">
                                <div className="relative">
                                    <div className="h-24 w-24 rounded-full bg-primary-100 flex items-center justify-center border-2 border-primary-200 overflow-hidden">
                                        {profileImage ? (
                                            <img src={profileImage} alt="Profile" className="h-full w-full object-cover" />
                                        ) : (
                                            <User className="h-12 w-12 text-primary-600" />
                                        )}
                                    </div>
                                    <label
                                        htmlFor="profile-upload"
                                        className="absolute bottom-0 right-0 h-8 w-8 bg-primary-600 rounded-full flex items-center justify-center cursor-pointer hover:bg-primary-700 transition-colors"
                                    >
                                        <Upload className="h-4 w-4 text-white" />
                                        <input
                                            id="profile-upload"
                                            type="file"
                                            accept="image/*"
                                            onChange={handleImageUpload}
                                            className="hidden"
                                        />
                                    </label>
                                </div>
                                <div>
                                    <p className="text-sm text-slate-700 mb-1">
                                        JPG, PNG or GIF. Max size 2MB
                                    </p>
                                    {profileImage && (
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => setProfileImage(null)}
                                        >
                                            Remove Photo
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </Card>

                        {/* Personal Information */}
                        <Card className="p-6">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="h-10 w-10 bg-primary-100 rounded-lg flex items-center justify-center">
                                    <User className="h-5 w-5 text-primary-600" />
                                </div>
                                <div>
                                    <h2 className="text-xl font-bold text-slate-900">Personal Information</h2>
                                    <p className="text-sm text-slate-500">Update your personal details</p>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-2">
                                        Full Name *
                                    </label>
                                    <input
                                        type="text"
                                        value={profile.name}
                                        onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-2">
                                        Email Address *
                                    </label>
                                    <input
                                        type="email"
                                        value={profile.email}
                                        onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-2">
                                        Phone Number
                                    </label>
                                    <input
                                        type="tel"
                                        value={profile.phone}
                                        onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-2">
                                        Company
                                    </label>
                                    <input
                                        type="text"
                                        value={profile.company}
                                        onChange={(e) => setProfile({ ...profile, company: e.target.value })}
                                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-2">
                                        Job Title
                                    </label>
                                    <input
                                        type="text"
                                        value={profile.jobTitle}
                                        onChange={(e) => setProfile({ ...profile, jobTitle: e.target.value })}
                                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                                    />
                                </div>
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-slate-700 mb-2">
                                        Bio
                                    </label>
                                    <textarea
                                        value={profile.bio}
                                        onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
                                        rows={4}
                                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                                        placeholder="Tell us about yourself..."
                                    />
                                </div>
                            </div>

                            <div className="pt-6 flex justify-end">
                                <Button onClick={handleSaveProfile}>
                                    Save Profile Changes
                                </Button>
                            </div>
                        </Card>
                    </div>
                )}

                {/* Account Settings Tab */}
                {activeTab === 'account' && (
                    <div className="space-y-6 animate-fade-in">
                        {/* Password Management */}
                        <Card className="p-6">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="h-10 w-10 bg-red-100 rounded-lg flex items-center justify-center">
                                    <Lock className="h-5 w-5 text-red-600" />
                                </div>
                                <div>
                                    <h2 className="text-xl font-bold text-slate-900">Password Management</h2>
                                    <p className="text-sm text-slate-500">Manage your password and security settings</p>
                                </div>
                            </div>

                            <div className="space-y-4">
                                <div className="flex items-center justify-between py-3 border-b border-slate-200">
                                    <div>
                                        <p className="font-medium text-slate-900">Password</p>
                                        <p className="text-sm text-slate-500">Last changed 30 days ago</p>
                                    </div>
                                    <Button variant="outline" onClick={handleChangePassword}>
                                        Change Password
                                    </Button>
                                </div>
                                <div className="flex items-center justify-between py-3">
                                    <div>
                                        <p className="font-medium text-slate-900">Two-Factor Authentication</p>
                                        <p className="text-sm text-slate-500">Add an extra layer of security to your account</p>
                                    </div>
                                    <Button variant="outline">
                                        Enable 2FA
                                    </Button>
                                </div>
                            </div>
                        </Card>

                        {/* Notification Settings */}
                        <Card className="p-6">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center">
                                    <Bell className="h-5 w-5 text-blue-600" />
                                </div>
                                <div>
                                    <h2 className="text-xl font-bold text-slate-900">Notifications</h2>
                                    <p className="text-sm text-slate-500">Configure how you receive notifications</p>
                                </div>
                            </div>

                            <div className="space-y-4">
                                <div className="flex items-center justify-between py-3">
                                    <div>
                                        <p className="font-medium text-slate-900">Email Notifications</p>
                                        <p className="text-sm text-slate-500">Receive updates via email</p>
                                    </div>
                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={notifications.email}
                                            onChange={(e) => setNotifications({ ...notifications, email: e.target.checked })}
                                            className="sr-only peer"
                                        />
                                        <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                                    </label>
                                </div>
                                <div className="flex items-center justify-between py-3">
                                    <div>
                                        <p className="font-medium text-slate-900">Push Notifications</p>
                                        <p className="text-sm text-slate-500">Receive push notifications in browser</p>
                                    </div>
                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={notifications.push}
                                            onChange={(e) => setNotifications({ ...notifications, push: e.target.checked })}
                                            className="sr-only peer"
                                        />
                                        <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                                    </label>
                                </div>
                                <div className="flex items-center justify-between py-3">
                                    <div>
                                        <p className="font-medium text-slate-900">Analysis Complete</p>
                                        <p className="text-sm text-slate-500">Notify when document analysis is complete</p>
                                    </div>
                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={notifications.analysis}
                                            onChange={(e) => setNotifications({ ...notifications, analysis: e.target.checked })}
                                            className="sr-only peer"
                                        />
                                        <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                                    </label>
                                </div>
                                <div className="pt-4 flex justify-end">
                                    <Button onClick={handleSaveNotifications}>
                                        Save Notification Settings
                                    </Button>
                                </div>
                            </div>
                        </Card>

                        {/* Appearance Settings */}
                        <Card className="p-6">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="h-10 w-10 bg-purple-100 rounded-lg flex items-center justify-center">
                                    <Globe className="h-5 w-5 text-purple-600" />
                                </div>
                                <div>
                                    <h2 className="text-xl font-bold text-slate-900">Appearance & Theme</h2>
                                    <p className="text-sm text-slate-500">Customize your interface preferences</p>
                                </div>
                            </div>

                            <div className="space-y-4">
                                <div className="flex items-center justify-between py-3">
                                    <div>
                                        <p className="font-medium text-slate-900">Dark Mode</p>
                                        <p className="text-sm text-slate-500">Switch to dark theme for reduced eye strain</p>
                                    </div>
                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={darkMode}
                                            onChange={(e) => setDarkMode(e.target.checked)}
                                            className="sr-only peer"
                                        />
                                        <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                                    </label>
                                </div>
                                <div className="flex items-center justify-between py-3 border-t border-slate-200">
                                    <div>
                                        <p className="font-medium text-slate-900">Language</p>
                                        <p className="text-sm text-slate-500">Select your preferred language</p>
                                    </div>
                                    <select className="px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500">
                                        <option>English</option>
                                        <option>Spanish</option>
                                        <option>French</option>
                                        <option>German</option>
                                    </select>
                                </div>
                                <div className="flex items-center justify-between py-3 border-t border-slate-200">
                                    <div>
                                        <p className="font-medium text-slate-900">Time Zone</p>
                                        <p className="text-sm text-slate-500">Set your local time zone</p>
                                    </div>
                                    <select className="px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500">
                                        <option>UTC-5 (EST)</option>
                                        <option>UTC-8 (PST)</option>
                                        <option>UTC+0 (GMT)</option>
                                        <option>UTC+1 (CET)</option>
                                    </select>
                                </div>
                            </div>
                        </Card>

                        {/* Danger Zone - Delete Account */}
                        <Card className="p-6 border-danger-200 bg-danger-50/30">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="h-10 w-10 bg-danger-100 rounded-lg flex items-center justify-center">
                                    <Trash2 className="h-5 w-5 text-danger-600" />
                                </div>
                                <div>
                                    <h2 className="text-xl font-bold text-slate-900">Danger Zone</h2>
                                    <p className="text-sm text-slate-500">Irreversible account actions</p>
                                </div>
                            </div>

                            <div className="bg-white rounded-lg p-4 border border-danger-200">
                                <div className="flex items-start justify-between">
                                    <div>
                                        <p className="font-medium text-slate-900">Delete Account</p>
                                        <p className="text-sm text-slate-600 mt-1">
                                            Once you delete your account, there is no going back. All your data will be permanently removed.
                                        </p>
                                    </div>
                                    <Button variant="danger" onClick={handleDeleteAccount} className="ml-4">
                                        Delete Account
                                    </Button>
                                </div>
                            </div>
                        </Card>
                    </div>
                )}
            </div>
        </DashboardLayout>
    );
};
